import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { flaggedReasons, FlaggedReasonType } from "~/lib/bin";
import {
  SourceAndDestinationBinDto,
  StartBinMaintenanceResponseDto,
  TemperatureZone
} from "~/types/api";

type MovementTaskState =
  | "CleaningBin"
  | "MovingProduct"
  | "ConfirmingMovement"
  | null;

type NullableSourceAndDestinationBin = {
  sourceAndDestinationBin?: SourceAndDestinationBinDto | null;
};

export type InventoryMovementState = Omit<
  StartBinMaintenanceResponseDto,
  "inventoryMovements"
> & {
  inventoryMovements: NullableSourceAndDestinationBin;
};

export type MaintenanceSessionConfig = {
  tempZone: TemperatureZone | null;
  flaggedReasons: FlaggedReasonType[];
};

export const defaultMaintenanceConfig: MaintenanceSessionConfig = {
  tempZone: null,
  flaggedReasons: [...flaggedReasons]
};

type BinMaintenanceWorkstationState = {
  movementTaskState: MovementTaskState;
  movementData: InventoryMovementState | undefined;
  maintenanceConfig: MaintenanceSessionConfig;
  selectedPort: number | undefined;
  isDestinationBinReady: boolean;
  isSourceBinReady: boolean;
  isMoveErrorModalOpen: boolean;
  isMoveAdjustModalOpen: boolean;
};

const initialState: BinMaintenanceWorkstationState = {
  movementTaskState: null,
  movementData: undefined,
  maintenanceConfig: defaultMaintenanceConfig,
  selectedPort: undefined,
  isDestinationBinReady: false,
  isSourceBinReady: false,
  isMoveErrorModalOpen: false,
  isMoveAdjustModalOpen: false
};

export const binMaintenanceWorkstationSlice = createSlice({
  name: "binMaintenanceWorkstation",
  initialState,
  reducers: {
    setTaskState(state, { payload }: PayloadAction<MovementTaskState>) {
      state.movementTaskState = payload;
    },
    setMovementData(state, { payload }: PayloadAction<InventoryMovementState>) {
      state.movementData = payload;
    },
    setMaintenanceConfig(
      state,
      { payload }: PayloadAction<Partial<MaintenanceSessionConfig>>
    ) {
      state.maintenanceConfig = {
        ...state.maintenanceConfig,
        ...payload
      };
    },
    resetMaintenanceConfig(state) {
      state.maintenanceConfig = defaultMaintenanceConfig;
    },
    setSourceDestinationBins(
      state,
      { payload }: PayloadAction<NullableSourceAndDestinationBin>
    ) {
      // This action primarily targets updating movementData.inventoryMovements.
      // By not updating movementData.binCleaningPorts unless we're just
      // starting bin maintenance, we avoid side effects being triggered.
      if (state.movementData) {
        state.movementData.inventoryMovements = payload;
      } else {
        state.movementData = {
          binCleaningPorts: [],
          inventoryMovements: payload
        };
      }
    },
    setSelectedPort(state, { payload }: PayloadAction<number>) {
      state.selectedPort = payload;
    },
    setIsDestinationBinReady(state, { payload }: PayloadAction<boolean>) {
      state.isDestinationBinReady = payload;
    },
    setIsSourceBinReady(state, { payload }: PayloadAction<boolean>) {
      state.isSourceBinReady = payload;
    },
    setIsMoveErrorModalOpen(state, { payload }: PayloadAction<boolean>) {
      state.isMoveErrorModalOpen = payload;
    },
    setIsMoveAdjustModalOpen(state, { payload }: PayloadAction<boolean>) {
      state.isMoveAdjustModalOpen = payload;
    }
  }
});
export const {
  setTaskState,
  setMovementData,
  setMaintenanceConfig,
  resetMaintenanceConfig,
  setSourceDestinationBins,
  setSelectedPort,
  setIsDestinationBinReady,
  setIsSourceBinReady,
  setIsMoveErrorModalOpen,
  setIsMoveAdjustModalOpen
} = binMaintenanceWorkstationSlice.actions;
